/**
 * AnnotationEditor Component
 *
 * Waveform-based annotation editor using wavesurfer.js.
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Select,
  SelectItem,
  Spinner,
} from '@heroui/react';
import { ArrowLeft, Pause, Play, Save, Trash2 } from 'lucide-react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions';

import { useAudioFiles, useGetDownloadUrlMutation } from '@/lib/api/hooks/audio';
import {
  useAnnotationSet,
  useCreateAnnotation,
  useUpdateAnnotation,
  useDeleteAnnotation,
} from '@/lib/api/hooks/annotations';
import { formatDuration } from '@/lib/design-system';
import type { AnnotationResponse, AnnotationCreate } from '@/api';

interface ArtifactType {
  name: string;
  color: string;
}

interface AnnotationEditorProps {
  datasetId: string;
  annotationSetId: string;
  artifactTypes: ArtifactType[];
  onBack: () => void;
}

interface PendingRegion {
  region: Region;
  start_ms: number;
  end_ms: number;
}

export function AnnotationEditor({
  datasetId,
  annotationSetId,
  artifactTypes,
  onBack,
}: AnnotationEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const regionsRef = useRef<RegionsPlugin | null>(null);

  const [selectedAudioId, setSelectedAudioId] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [pendingRegion, setPendingRegion] = useState<PendingRegion | null>(null);
  const [selectedArtifactType, setSelectedArtifactType] = useState<string>(
    artifactTypes[0]?.name || ''
  );

  const { data: audioFiles } = useAudioFiles(datasetId);
  const { data: annotationSetData, isLoading: isLoadingAnnotations } = useAnnotationSet(
    datasetId,
    annotationSetId
  );
  const getDownloadUrl = useGetDownloadUrlMutation(datasetId);
  const createAnnotation = useCreateAnnotation(datasetId, annotationSetId);
  const updateAnnotation = useUpdateAnnotation(datasetId, annotationSetId);
  const deleteAnnotation = useDeleteAnnotation(datasetId, annotationSetId);

  const isPublished = annotationSetData?.status === 'published';
  const readyFiles = useMemo(
    () => audioFiles?.items?.filter((f) => f.processing_status === 'ready') ?? [],
    [audioFiles]
  );

  const annotationsForFile = useMemo(() => {
    if (!annotationSetData?.annotations || !selectedAudioId) return [];
    return annotationSetData.annotations.filter(
      (a) => a.audio_file_id === selectedAudioId
    );
  }, [annotationSetData?.annotations, selectedAudioId]);

  const getColorForType = useCallback(
    (type: string) => {
      const found = artifactTypes.find((t) => t.name === type);
      return found?.color || '#6366f1';
    },
    [artifactTypes]
  );

  // Initialize wavesurfer
  useEffect(() => {
    if (!containerRef.current) return;

    const regions = RegionsPlugin.create();
    regionsRef.current = regions;

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#a1a1aa',
      progressColor: '#3b82f6',
      cursorColor: '#3b82f6',
      cursorWidth: 2,
      height: 128,
      normalize: true,
      plugins: [regions],
    });

    wavesurferRef.current = ws;

    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));
    ws.on('timeupdate', (time) => setCurrentTime(time));
    ws.on('decode', (d) => setDuration(d));

    // Handle region creation (only for drafts)
    regions.on('region-created', (region) => {
      if (isPublished) {
        region.remove();
        return;
      }

      // Check if this is a new user-created region (no data attached)
      if (!region.id.startsWith('annotation-')) {
        const start_ms = Math.round(region.start * 1000);
        const end_ms = Math.round(region.end * 1000);
        setPendingRegion({ region, start_ms, end_ms });
      }
    });

    return () => {
      ws.destroy();
    };
  }, [isPublished]);

  // Load audio when selection changes
  useEffect(() => {
    if (!selectedAudioId || !wavesurferRef.current) return;

    const loadAudio = async () => {
      setIsLoading(true);
      try {
        const result = await getDownloadUrl.mutateAsync(selectedAudioId);
        if (result?.download_url && wavesurferRef.current) {
          await wavesurferRef.current.load(result.download_url);
        }
      } catch {
        // Error handled by mutation
      } finally {
        setIsLoading(false);
      }
    };

    loadAudio();
  }, [selectedAudioId, getDownloadUrl]);

  // Sync annotations to regions
  useEffect(() => {
    if (!regionsRef.current || !wavesurferRef.current) return;

    // Clear existing regions
    regionsRef.current.clearRegions();

    // Add regions for existing annotations
    annotationsForFile.forEach((annotation) => {
      const color = getColorForType(annotation.artifact_type);
      regionsRef.current?.addRegion({
        id: `annotation-${annotation.id}`,
        start: annotation.start_ms / 1000,
        end: annotation.end_ms / 1000,
        color: `${color}40`,
        drag: !isPublished,
        resize: !isPublished,
        content: annotation.artifact_type,
      });
    });
  }, [annotationsForFile, getColorForType, isPublished]);

  // Handle region updates
  useEffect(() => {
    if (!regionsRef.current) return;

    const handleRegionUpdate = async (region: Region) => {
      if (isPublished) return;

      const annotationId = region.id.replace('annotation-', '');
      const annotation = annotationsForFile.find((a) => a.id === annotationId);

      if (annotation) {
        try {
          await updateAnnotation.mutateAsync({
            annotationId,
            data: {
              start_ms: Math.round(region.start * 1000),
              end_ms: Math.round(region.end * 1000),
            },
          });
        } catch {
          // Error handled by mutation
        }
      }
    };

    regionsRef.current.on('region-updated', handleRegionUpdate);

    return () => {
      regionsRef.current?.un('region-updated', handleRegionUpdate);
    };
  }, [annotationsForFile, updateAnnotation, isPublished]);

  const handlePlayPause = () => {
    wavesurferRef.current?.playPause();
  };

  const handleSaveRegion = async () => {
    if (!pendingRegion || !selectedAudioId || !selectedArtifactType) return;

    const annotationData: AnnotationCreate = {
      audio_file_id: selectedAudioId,
      artifact_type: selectedArtifactType,
      start_ms: pendingRegion.start_ms,
      end_ms: pendingRegion.end_ms,
    };

    try {
      await createAnnotation.mutateAsync(annotationData);
      pendingRegion.region.remove();
      setPendingRegion(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleCancelRegion = () => {
    if (pendingRegion) {
      pendingRegion.region.remove();
      setPendingRegion(null);
    }
  };

  const handleDeleteAnnotation = async (annotationId: string) => {
    if (isPublished) return;

    try {
      await deleteAnnotation.mutateAsync(annotationId);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="light"
          size="sm"
          startContent={<ArrowLeft className="h-4 w-4" />}
          onPress={onBack}
        >
          Back to Versions
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-default-500">
            Version {annotationSetData?.version}
          </span>
          <Chip
            size="sm"
            variant="flat"
            color={isPublished ? 'success' : 'warning'}
          >
            {annotationSetData?.status}
          </Chip>
        </div>
      </div>

      {/* Audio File Selector */}
      <Card>
        <CardBody>
          <Select
            label="Select Audio File"
            placeholder="Choose a file to annotate"
            selectedKeys={selectedAudioId ? [selectedAudioId] : []}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0] as string;
              setSelectedAudioId(key || '');
            }}
          >
            {readyFiles.map((file) => (
              <SelectItem key={file.id}>
                {file.original_filename}
              </SelectItem>
            ))}
          </Select>
        </CardBody>
      </Card>

      {/* Waveform */}
      {selectedAudioId && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                isIconOnly
                variant="flat"
                onPress={handlePlayPause}
                isDisabled={isLoading}
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </Button>
              <span className="text-sm font-mono">
                {formatDuration(currentTime * 1000)} / {formatDuration(duration * 1000)}
              </span>
            </div>
            {!isPublished && (
              <p className="text-sm text-default-500">
                Click and drag on the waveform to create annotations
              </p>
            )}
          </CardHeader>
          <CardBody>
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Spinner size="lg" />
              </div>
            )}
            <div
              ref={containerRef}
              className={isLoading ? 'invisible h-0' : ''}
            />
          </CardBody>
        </Card>
      )}

      {/* Pending Region Dialog */}
      {pendingRegion && (
        <Card className="border-2 border-primary">
          <CardBody className="space-y-4">
            <h4 className="font-semibold">New Annotation</h4>
            <div className="flex items-center gap-4">
              <span className="text-sm text-default-500">
                {formatDuration(pendingRegion.start_ms)} - {formatDuration(pendingRegion.end_ms)}
              </span>
              <Select
                label="Artifact Type"
                size="sm"
                className="max-w-xs"
                selectedKeys={selectedArtifactType ? [selectedArtifactType] : []}
                onSelectionChange={(keys) => {
                  const key = Array.from(keys)[0] as string;
                  setSelectedArtifactType(key || '');
                  // Update region color
                  if (pendingRegion.region && key) {
                    const color = getColorForType(key);
                    pendingRegion.region.setOptions({ color: `${color}40` });
                  }
                }}
              >
                {artifactTypes.map((type) => (
                  <SelectItem
                    key={type.name}
                    startContent={
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: type.color }}
                      />
                    }
                  >
                    {type.name}
                  </SelectItem>
                ))}
              </Select>
            </div>
            <div className="flex gap-2">
              <Button
                color="primary"
                size="sm"
                startContent={<Save className="h-4 w-4" />}
                onPress={handleSaveRegion}
                isLoading={createAnnotation.isPending}
              >
                Save
              </Button>
              <Button
                variant="flat"
                size="sm"
                onPress={handleCancelRegion}
              >
                Cancel
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Annotations List */}
      {selectedAudioId && annotationsForFile.length > 0 && (
        <Card>
          <CardHeader>
            <h3 className="font-semibold">
              Annotations ({annotationsForFile.length})
            </h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-2">
              {annotationsForFile.map((annotation) => (
                <AnnotationRow
                  key={annotation.id}
                  annotation={annotation}
                  color={getColorForType(annotation.artifact_type)}
                  isPublished={isPublished}
                  onDelete={() => handleDeleteAnnotation(annotation.id)}
                  isDeleting={deleteAnnotation.isPending}
                  onSeek={(ms) => {
                    if (wavesurferRef.current) {
                      wavesurferRef.current.seekTo(ms / 1000 / duration);
                    }
                  }}
                />
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Empty state */}
      {selectedAudioId && annotationsForFile.length === 0 && !isLoadingAnnotations && (
        <Card>
          <CardBody className="text-center py-8 text-default-500">
            No annotations for this file yet.
            {!isPublished && ' Click and drag on the waveform to create one.'}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function AnnotationRow({
  annotation,
  color,
  isPublished,
  onDelete,
  isDeleting,
  onSeek,
}: {
  annotation: AnnotationResponse;
  color: string;
  isPublished: boolean;
  onDelete: () => void;
  isDeleting: boolean;
  onSeek: (ms: number) => void;
}) {
  return (
    <div className="flex items-center justify-between p-2 rounded-lg hover:bg-default-100 transition-colors">
      <button
        type="button"
        className="flex items-center gap-3 flex-1 text-left"
        onClick={() => onSeek(annotation.start_ms)}
      >
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: color }}
        />
        <span className="font-medium">{annotation.artifact_type}</span>
        <span className="text-sm text-default-500 font-mono">
          {formatDuration(annotation.start_ms)} - {formatDuration(annotation.end_ms)}
        </span>
      </button>
      {!isPublished && (
        <Button
          size="sm"
          variant="light"
          color="danger"
          isIconOnly
          onPress={onDelete}
          isDisabled={isDeleting}
          aria-label={`Delete ${annotation.artifact_type} annotation`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
